'use client'

import Link from '@/components/link-locale'
import { Register, RequestRegister } from '@/lib/api/hooks/use-account'
import { Login } from '@/lib/api/hooks/use-account'
import { App, Button, Checkbox, Form, Input, Spin } from 'antd'
import { useTranslations } from 'next-intl'
import { siteConfig } from '@/config/site'
import React, { useEffect, useMemo, useState } from 'react'

export default function RegisterForm() {
  const t = useTranslations()
  App.useApp()

  const [form] = Form.useForm()
  const email = Form.useWatch('email', form)

  const [isEmailValid, setIsEmailValid] = useState(false)
  const [captchaLock, setCaptchaLock] = useState(false)
  const [countdown, setCountdown] = useState(0)

  const { mutateAsync: requestRegister, isPending: isRequestLoading } =
    RequestRegister()
  const { mutateAsync: register, isPending: isRegisterLoading } = Register()
  const { mutateAsync: login } = Login()

  const isCaptchaDisabled = !isEmailValid || captchaLock

  const spinning = isRequestLoading || isRegisterLoading

  const isCountingDown = useMemo(() => countdown > 0, [countdown])

  useEffect(() => {
    if (!form.isFieldTouched('email')) {
      return
    }
    form
      .validateFields(['email'])
      .then(() => {
        setIsEmailValid(true)
      })
      .catch(() => {
        setIsEmailValid(false)
      })
  }, [email, form])

  useEffect(() => {
    let timer
    if (isCountingDown) {
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setCaptchaLock(false)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => {
      if (timer) {
        clearInterval(timer)
      }
    }
  }, [isCountingDown])

  const onCaptchaClick = async () => {
    if (captchaLock) {
      return
    }
    try {
      setCaptchaLock(true)
      await requestRegister({ email })
      setCountdown(180)
    } catch (error) {
      console.error(error)
      setCaptchaLock(false)
    }
  }

  const onFormFinish = async values => {
    const payload = {
      ...values,
    }
    delete payload.agreement
    try {
      await register(payload)
      form.resetFields()
      await login({ email: values.email, password: values.password })
    } catch (error) {
      console.log('🚀 ~ onFormFinish ~ error:', error)
    }
  }

  return (
    <>
      <Form layout='vertical' colon={false} onFinish={onFormFinish} form={form}>
        <Form.Item
          label={t('common.email')}
          name='email'
          rules={[{ required: true }, { type: 'email' }]}
        >
          <Input
            className='h-10 text-sm font-normal'
            disabled={captchaLock}
            showCount={true}
            maxLength={siteConfig.validation.email.maxLength}
            autoComplete='email'
          />
        </Form.Item>
        <Form.Item
          label={t('common.captcha')}
          name='code'
          rules={[{ required: true }]}
        >
          <div className='flex gap-x-2.5'>
            <Form.Item
              name='code'
              rules={[
                { required: true },
                {
                  pattern: siteConfig.validation.code.pattern,
                  message: t('error.captchaPattern'),
                },
              ]}
              noStyle={true}
            >
              <Input className='h-10' autoComplete='captcha' />
            </Form.Item>
            <Button
              type='primary'
              className='h-10'
              onClick={onCaptchaClick}
              disabled={isCaptchaDisabled}
              loading={isRequestLoading}
            >
              {countdown > 0
                ? `${t('common.getCaptcha')} (${countdown}s)`
                : t('common.getCaptcha')}
            </Button>
          </div>
        </Form.Item>
        <Form.Item
          label={t('common.password')}
          name='password'
          validateFirst={true}
          rules={[
            { required: true },
            { type: 'string', min: 8, max: 20 },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
              message: t('error.passwordPattern'),
            },
          ]}
        >
          <Input.Password
            className='h-10 text-sm font-normal'
            maxLength={20}
            showCount={true}
            autoComplete='new-password'
          />
        </Form.Item>
        <Form.Item
          name='agreement'
          valuePropName='checked'
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('error.agreementRequired'))),
            },
          ]}
        >
          <Checkbox>
            <p className='text-xs font-normal leading-5'>
              {t('page.signUp.agreement1')}
              <Link
                href='/info/terms'
                target='_blank'
                className='mx-1 font-bold text-[#FF3C8E]'
              >
                {t('common.termsOfService')}
              </Link>
              {t('page.signUp.agreement2')}
              <Link
                href='/info/privacy'
                target='_blank'
                className='mx-1 font-bold text-[#FF3C8E]'
              >
                {t('common.privacyPolicy')}
              </Link>
            </p>
          </Checkbox>
        </Form.Item>
        <Form.Item>
          <Button
            type='primary'
            htmlType='submit'
            block={true}
            className='h-12 text-base font-semibold'
          >
            {t('common.register')}
          </Button>
        </Form.Item>
      </Form>
      <Spin spinning={spinning} fullscreen={true} />
    </>
  )
}
